import React from 'react';
import styled from 'styled-components';
import * as Switch from '@radix-ui/react-switch';
import HBTooltip from '../HBTooltip/HBTooltip';
import { Eye16, EyeCrossed16 } from '@honeybook/icons';
import { vars } from '../../theme';

export interface HBVisibilitySwitchProps
    extends Omit<Switch.SwitchProps, 'onChange'> {
    /** Per-state tooltip text; the matching string is shown based on the current checked value. */
    tooltipContent?: { checked: string; unchecked: string };
    /** Called with the new boolean state when the switch is toggled. */
    onChange: Switch.SwitchProps['onCheckedChange'];
    /** Accessible label for the switch; used as the base for the aria-label on the thumb. */
    label: string;
    'data-testid'?: string;
    /** HTML id attribute on the root element; also used as a fallback for data-testid. */
    id?: string;
    /** CSS class applied to the root element. */
    className?: string;
}

const HBVisibilitySwitch = ({
    checked,
    onChange,
    disabled,
    tooltipContent,
    label,
    'data-testid': dataTestid,
    id,
    className
}: HBVisibilitySwitchProps) => {
    const switchCmp = (
        <Root
            checked={checked}
            onCheckedChange={onChange}
            disabled={disabled}
            // this is required to preserve existing testim.io functionality
            data-testid={dataTestid || `${id}-label`}
            id={id}
            className={className}
        >
            <Thumb
                disabled={disabled}
                aria-label={
                    checked
                        ? `${label} visibility on`
                        : `${label} visibility off`
                }
            >
                {checked ? (
                    <Eye16 hoverColor={vars.colors.text.secondary} />
                ) : (
                    <EyeCrossed16 hoverColor={vars.colors.text.secondary} />
                )}
            </Thumb>
        </Root>
    );

    return (
        <HBTooltip
            target={switchCmp}
            placement="top"
            disabled={!tooltipContent}
        >
            {tooltipContent &&
                (checked ? tooltipContent.checked : tooltipContent.unchecked)}
        </HBTooltip>
    );
};

export default HBVisibilitySwitch;

const Root = styled(Switch.Root)<{ disabled: boolean }>`
    border: none;
    background: none !important;
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    ${props => props.disabled && 'opacity: 0.3'};
    line-height: 1;
    outline: revert;
`;

const Thumb = styled(Switch.Thumb)<{ disabled: boolean }>`
    // So svg is vertically centered inside icon
    i {
        display: flex;
    }
`;
